import { Card } from '@/components/ui/card'
import avatarBackground from '@/assets/dashboard/AvatarBackground.png'
import { ProfileWelcomeCardBaseProps, ProfileWelcomeCardBase } from './ProfileWelcomeCardBase'
import { getCryptoIcon, DEFAULT_BUY_ICONS, DEFAULT_SELL_ICONS } from './ProfileWelcomeCardAssets'

/**
 * Desktop version of ProfileWelcomeCard
 * Uses base logic but has desktop-specific UI with assets display
 */
export const ProfileWelcomeCardDesktop = ({ profile }: ProfileWelcomeCardBaseProps) => {
  const displayName = ProfileWelcomeCardBase.getDisplayName(profile)

  return (
    <div className="flex">
      <Card variant="default" className="border border-border pt-[24px] pb-[24px] px-4 sm:px-6 relative overflow-hidden flex-1 flex flex-col bg-[rgba(255,255,255,0.7)] dark:bg-[rgba(28,28,28,0.7)]">
        <div
          className="absolute inset-0 opacity-100"
          style={{
            backgroundImage: `url(${avatarBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        <div className="relative z-10 flex flex-col flex-1">
          {/* Desktop Layout - Left: Avatar + Text, Right: Assets */}
          <div className="flex items-start justify-between gap-4">
            {/* Left Column - Avatar and Welcome Text (vertical) */}
            <div className="flex flex-col items-start justify-between h-[168px]">
              {/* Avatar */}
              <div className="flex-shrink-0">
{profile?.profile_picture_url ? (
                    <img 
                      src={profile.profile_picture_url}
                    alt="User profile" 
                    className="w-[56px] h-[56px] rounded-full object-cover"
                  />
                ) : (
                  <div className="w-[56px] h-[56px] rounded-full bg-primary/10 flex items-center justify-center text-xl font-bold text-primary border border-primary/20">
                    {profile?.first_name?.[0]}{profile?.last_name?.[0] || ''}
                  </div>
                )}
              </div>
              
              {/* Welcome Text */}
              <div className="flex flex-col items-start">
                <p className="font-plus-jakarta font-normal text-[24px] leading-[32px] tracking-[-0.28px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
                  Welcome back,
                </p>
                <h3 className="font-plus-jakarta font-semibold text-[24px] leading-[32px] tracking-[-0.28px] text-[#2d2923] dark:text-[#fdfdfd] whitespace-nowrap">
                  {displayName}
                </h3>
              </div>
            </div>

            {/* Right Column - Assets (horizontal, aligned with avatar top) */}
            <div className="flex items-start gap-4 flex-shrink-0">
              {/* Assets to Buy - always show icons as in design */}
              <div className="flex flex-col gap-2">
                <p className="font-plus-jakarta font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  Assets to Buy:
                </p>
                <div className="flex items-center gap-0 pl-0 pr-1">
                  {profile?.companies_of_interest_buy && profile.companies_of_interest_buy.length > 0 ? (
                    <>
                      {profile.companies_of_interest_buy.slice(0, 3).map((asset, index) => {
                        const icon = getCryptoIcon(asset)
                        if (!icon) return null
                        return (
                          <div key={index} className="w-6 h-6 -mr-1 overflow-clip rounded-full relative shrink-0">
                            <img 
                              src={icon.main} 
                              alt={asset}
                              className="block max-w-none w-full h-full"
                            />
                            {icon.overlay && (
                              <div className="absolute inset-[16.34%_24.42%_16.33%_24.5%]">
                                <img 
                                  src={icon.overlay} 
                                  alt=""
                                  className="block max-w-none w-full h-full"
                                />
                              </div>
                            )}
                          </div>
                        )
                      })}
                      {profile.companies_of_interest_buy.length > 3 && (
                        <div className="w-6 h-6 -mr-1 rounded-full bg-black flex items-center justify-center shrink-0">
                          <span className="font-plus-jakarta font-semibold text-[11px] leading-[12px] text-white">
                            +{profile.companies_of_interest_buy.length - 3}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    // Show examples from design if no data
                    <>
                      {DEFAULT_BUY_ICONS.map((icon, index) => (
                        <div key={index} className="w-6 h-6 -mr-1 overflow-clip rounded-full relative shrink-0">
                          <img 
                            src={icon.main} 
                            alt=""
                            className="block max-w-none w-full h-full"
                          />
                          {icon.overlay && (
                            <div className="absolute inset-[16.34%_24.42%_16.33%_24.5%]">
                              <img 
                                src={icon.overlay} 
                                alt=""
                                className="block max-w-none w-full h-full"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="w-6 h-6 -mr-1 rounded-full bg-black flex items-center justify-center shrink-0">
                        <span className="font-plus-jakarta font-semibold text-[11px] leading-[12px] text-white">
                          +5
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Assets to Sell - always show icons as in design */}
              <div className="flex flex-col gap-2">
                <p className="font-plus-jakarta font-medium text-[13px] leading-[18px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
                  Assets to Sell:
                </p>
                <div className="flex items-center gap-0 pl-0 pr-1">
                  {profile?.companies_of_interest_sell && profile.companies_of_interest_sell.length > 0 ? (
                    <>
                      {profile.companies_of_interest_sell.slice(0, 3).map((asset, index) => {
                        const icon = getCryptoIcon(asset)
                        if (!icon) return null
                        return (
                          <div key={index} className="w-6 h-6 -mr-1 overflow-clip rounded-full relative shrink-0">
                            <img 
                              src={icon.main} 
                              alt={asset}
                              className="block max-w-none w-full h-full"
                            />
                            {icon.overlay && (
                              <div className="absolute inset-[20.55%_28.84%_24.41%_31.02%]">
                                <img 
                                  src={icon.overlay} 
                                  alt=""
                                  className="block max-w-none w-full h-full"
                                />
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </>
                  ) : (
                    // Show examples from design if no data
                    <>
                      {DEFAULT_SELL_ICONS.map((icon, index) => (
                        <div key={index} className="w-6 h-6 -mr-1 overflow-clip rounded-full relative shrink-0">
                          <img 
                            src={icon.main} 
                            alt=""
                            className="block max-w-none w-full h-full"
                          />
                          {icon.overlay && (
                            <div className={`absolute ${icon.overlayPosition || 'inset-[16.34%_24.42%_16.33%_24.5%]'}`}>
                              <img 
                                src={icon.overlay} 
                                alt=""
                                className="block max-w-none w-full h-full"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}

